import React, { useMemo, useState } from "react";
import { TrendCurveChartData } from "../../../interface/DesignerInterface";
import {
  formatTrendCurvePeriodLabel,
  sortTrendCurvePeriodsForDisplay,
} from "../../../utils/trendSidebarGraphUtils/trendCurveDataUtils";
import { formatTrendHeaderChips } from "../../../utils/designerPage-utils";

type TrendTableProps = {
  trendsCurveRawData: {
    tNo: number;
    Trend: string;
    data: { period: string; contribution: number }[];
  }[];
  curveWindowType?: string;
  datasets?: TrendCurveChartData["datasets"];
  selectedTNo?: number | null;
  onRowHover?: (datasetIndex: number | null) => void;
  onRowClick?: (tNo: number) => void;
};

const TrendTable: React.FC<TrendTableProps> = ({
  trendsCurveRawData,
  datasets = [],
  selectedTNo = null,
  onRowHover,
  onRowClick,
}) => {
  const [hoveredTNo, setHoveredTNo] = useState<number | null>(null);

  const tableData = useMemo(
    () => [...trendsCurveRawData].sort((a, b) => a.tNo - b.tNo),
    [trendsCurveRawData]
  );

  const colorByTNo = useMemo(() => {
    const map = new Map<number, string>();
    datasets.forEach(dataset => {
      if (dataset.tNo != null) {
        map.set(dataset.tNo, dataset.borderColor);
      }
    });
    return map;
  }, [datasets]);

  const datasetIndexByTNo = useMemo(() => {
    const map = new Map<number, number>();
    datasets.forEach((dataset, index) => {
      if (dataset.tNo != null) {
        map.set(dataset.tNo, index);
      }
    });
    return map;
  }, [datasets]);

  const sortedColumns = tableData[0]?.data
    ? sortTrendCurvePeriodsForDisplay(tableData[0].data)
    : [];

  const gridColumns = `minmax(80px, 0.9fr) minmax(160px, 1.4fr) repeat(${sortedColumns.length || 0}, minmax(64px, 1fr))`;

  const handleRowEnter = (tNo: number) => {
    const datasetIndex = datasetIndexByTNo.get(tNo) ?? null;
    setHoveredTNo(tNo);
    if (selectedTNo == null && datasetIndex != null) {
      onRowHover?.(datasetIndex);
    }
  };

  const handleRowLeave = () => {
    setHoveredTNo(null);
    if (selectedTNo == null) {
      onRowHover?.(null);
    }
  };

  const handleRowClick = (tNo: number) => {
    onRowClick?.(tNo);
  };

  return (
    <div className="px-1 pb-8 font-poppins">
      <div className="min-w-full border border-gray-300 bg-white">
        <div
          className="grid whitespace-nowrap border-b border-gray-300 bg-white text-center text-sm font-bold text-gray-800"
          style={{ gridTemplateColumns: gridColumns }}
        >
          <div className="overflow-hidden border-r border-gray-300 p-2 text-center">
            Trend No.
          </div>
          <div className="border-r border-gray-300 p-2 text-center">Trend</div>
          {sortedColumns.map((item, index) => (
            <div
              key={index}
              className="border-r border-gray-300 p-2 text-center"
            >
              {formatTrendCurvePeriodLabel(item.period)}
            </div>
          ))}
        </div>

        {tableData.map(row => {
          const lineColor = colorByTNo.get(row.tNo) ?? "#9ca3af";
          const isHovered = hoveredTNo === row.tNo;
          const isSelected = selectedTNo === row.tNo;

          return (
            <div
              key={row.tNo}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              className={`grid cursor-pointer border-b border-gray-300 transition-colors ${
                isSelected
                  ? "bg-secondary/15 ring-1 ring-inset ring-secondary/30"
                  : isHovered
                    ? "bg-gray-100"
                    : "hover:bg-gray-200"
              }`}
              style={{
                gridTemplateColumns: gridColumns,
                boxShadow: `inset 4px 0 0 ${lineColor}`,
              }}
              onMouseEnter={() => handleRowEnter(row.tNo)}
              onMouseLeave={handleRowLeave}
              onClick={() => handleRowClick(row.tNo)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleRowClick(row.tNo);
                }
              }}
            >
              <div className="border-r border-gray-300 p-2 text-center font-medium text-gray-800">
                {row.tNo}
              </div>
              <div
                className="border-r border-gray-300 p-2 text-center text-sm text-gray-700"
                title={row.Trend}
              >
                {formatTrendHeaderChips(row.Trend)}
              </div>
              {sortedColumns.map((column, index) => {
                const cell = row.data.find(
                  point => point.period === column.period
                );
                return (
                  <div
                    key={index}
                    className="border-r border-gray-300 p-2 text-center text-sm text-gray-700"
                  >
                    {cell?.contribution ?? 0}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrendTable;
